import React, { useCallback, useMemo, useRef } from 'react';
import Chart from './Chart';
import { STRINGS } from '../utils/constants';
import { buildMainChartOptions, buildMainChartSeries } from '../utils/chartOptions';

const MainChart = ({ data, isDarkMode, periodLabel, dateRange, activeServiceId = null }) => {
  const chartRef = useRef(null);
  const isMonthMode = dateRange === 'month';

  // When a specific service is active, the bar chart drills down to that
  // single service; null means "All Services" → render every service stacked.
  const displayedServices = useMemo(() => {
    const all = data.serviceSummary.map((srv) => ({ id: srv.id, name: srv.name, color: srv.color }));
    if (activeServiceId == null) return all;
    const match = all.find((s) => s.id === activeServiceId);
    return match ? [match] : all;
  }, [data.serviceSummary, activeServiceId]);

  const handleMount = useCallback((chart) => {
    chartRef.current = chart;
  }, []);

  const options = useMemo(
    () => buildMainChartOptions({ data, displayedServices, isDarkMode, onMount: handleMount }),
    [data, displayedServices, isDarkMode, handleMount]
  );

  const series = useMemo(
    () => buildMainChartSeries({ data, displayedServices }),
    [data, displayedServices]
  );

  const handleZoomIn = () =>
    chartRef.current?.zoomX?.(0, Math.max(1, Math.ceil(data.labels.length * 0.5)));
  const handleResetZoom = () => chartRef.current?.resetSeries?.();

  return (
    <div className="glass-card chart-card chart-card-full mb-4">
      <div className="card-title-bar">
        <div>
          <h3>{STRINGS.mainChartTitle}</h3>
          <p className="chart-subtitle">{STRINGS.mainChartSubtitle}</p>
        </div>
        <div className="chart-meta-right">
          {periodLabel && (
            <span className="period-badge">
              <i className="fa fa-calendar-o" style={{ marginInlineEnd: '6px' }} />
              {periodLabel}
            </span>
          )}
          <div className="chart-toolbar-btns">
            <button type="button" className="chart-tool-btn" onClick={handleZoomIn} title={STRINGS.zoomIn}>
              <i className="fa fa-search-plus" />
            </button>
            <button type="button" className="chart-tool-btn" onClick={handleResetZoom} title={STRINGS.resetZoom}>
              <i className="fa fa-refresh" />
            </button>
          </div>
        </div>
      </div>

      <div className={`chart-scroll-wrapper ${isMonthMode ? 'chart-scroll-active' : ''}`}>
        <div className={isMonthMode ? 'chart-inner-month' : 'chart-inner-normal'}>
          <Chart options={options} series={series} type="bar" height={560} />
        </div>
      </div>

      {isMonthMode && (
        <p className="scroll-hint">
          <i className="fa fa-hand-o-right" style={{ marginInlineEnd: '6px' }} />
          اسحب لليمين أو اليسار لعرض جميع الأيام
        </p>
      )}
    </div>
  );
};

export default MainChart;
